import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	createTheme,
	StyledEngineProvider,
	ThemeProvider,
} from "@mui/material/styles";
import clsx from "clsx";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";

import * as Api from "./Api";
import * as Flags from "./lib/Flags";
import * as NavigationUtils from "./lib/util/NavigationUtils";
import * as UserSelectors from "./store/User/User.selector";
import * as LocaleUtils from "./lib/util/LocaleUtils";
import * as NotificationUtils from "lib/util/NotificationUtils";
import * as StorageUtils from "./lib/util/StorageUtils";
import * as UserUtils from "./lib/util/UserUtils";
import * as EnvironmentUtils from "./lib/util/EnvironmentUtils";
import * as AuthUtils from "./lib/util/AuthUtils";
import { isFiveKeysTheme } from "./lib/util/ThemeUtils";
import { TRENDS_AVAILABLE_KEY } from "./StorageKeys";

import routerManager from "./Routing";
import PrivacyModal from "./components/custom/PrivacyModal/PrivacyModal";
import Background from "./components/common/Background/Background";
import SpinnerOverlay from "./components/custom/SpinnerOverlay/SpinnerOverlay";
import ScrollToTop from "./components/custom/ScrollToTop/ScrollToTop";
import FatalError from "./components/custom/FatalError/FatalError";
import DebugArea from "./components/common/DebugArea/DebugArea";
import Header from "./components/custom/Header/Header";
import SplashNotification from "./components/custom/SplashNotification/SplashNotification";
import NotificationBanner from "./components/custom/NotificationBanner/NotificationBanner";
import ErrorBoundary from "./components/util/ErrorBoundary/ErrorBoundary";
import Button from "./components/common/Button/Button";
import { AboveNavContext } from "./components/nav/Navigation/Navigation";
import { useDispatch, useSelector } from "./lib/hook/ReduxHooks";
import {
	useHeaderVisibility,
	useNotificationVisible,
	useScrollTopVisibility,
	useIsRouteAccessible,
} from "./lib/hook/NavigationHooks";
import { useFlag } from "./lib/hook/MiscHooks";
import { ViewContext } from "lib/util/ReactUtils";
import { updateUserObject } from "./store/User/User.action";
import * as FleetSelector from "./store/Fleet/Fleet.selector";
import LoadingSpinner from "./components/common/LoadingSpinner/LoadingSpinner";
import * as strings from "./strings";

import "./components/util/FiveKeysTheme/FiveKeysTheme";
import "./components/util/BrightmileTheme/BrightmileTheme";
import FiveKeysTheme from "./components/util/FiveKeysTheme/FiveKeysTheme";
import BrightmileTheme from "./components/util/BrightmileTheme/BrightmileTheme";
import { useTranslated } from "lib/hook/LocaleHooks";

const theme = createTheme({
	palette: {
		primary: {
			main: "#f9c509",
		},
		secondary: {
			main: "#f9c509",
		},
	},
});

const RouterContent = ({ routes, renderId }: any) => {
	const headerVisible = useHeaderVisibility();
	const scrollToTopVisible = useScrollTopVisibility();
	const routeAccessible = useIsRouteAccessible();
	const localeContext = useContext(LocaleUtils.LocaleContext);
	const dispatch = useDispatch();
	const isFiveKeys = isFiveKeysTheme();
	const [loadingData, setLoadingData] = useState(false);
	const [portalNotification, setPortalNotification] =
		useState<NotificationUtils.PortalNotification>({});
	const bannerNotificationVisible = useNotificationVisible(
		NotificationUtils.NOTIFICATION_TYPE_BANNER,
		portalNotification.level,
	);
	const [translatedFiveKeysMessage] = useTranslated(
		strings.FIVE_KEYS_VARIANT_POPUP_COPY,
	);

	useEffect(() => {
		if (isFiveKeys) {
			document.body.classList.remove("--variant-brightmile");
			document.body.classList.add("--variant-5keys");
		} else {
			document.body.classList.add("--variant-brightmile");
			document.body.classList.remove("--variant-5keys");
		}
	}, [isFiveKeys]);

	useEffect(() => {
		const getNotifcationLevel = async () => {
			const notification = await Api.fetchPortalNotificationLevel.submit({
				options: { cache: false },
			});
			setPortalNotification(notification.data);
		};

		const interval = window.setInterval(
			getNotifcationLevel,
			NotificationUtils.POLLING_INTERVAL,
		);
		getNotifcationLevel();

		return () => window.clearInterval(interval);
	}, [setPortalNotification, localeContext]);

	const {
		fleets,
		fleet,
		additionalFleets,
		loggedInUser,
		hasTrends,
		appType,
		isFiveKeysUser,
	} = useSelector((state) => {
		const loggedInUser = UserSelectors.selectLoggedInUser(state);

		return {
			loggedInUser: loggedInUser,
			additionalFleets: loggedInUser?.additionalFleets,
			fleets: FleetSelector.getAllFleets(state),
			fleet: FleetSelector.selectCurrentScopeFleet(state),
			hasTrends: UserSelectors.selectCurrentScopeHasTrends(state),
			appType: UserSelectors.selectCurrentScopeAppType(state),
			isFiveKeysUser:
				!AuthUtils.isSuperAdmin() && UserUtils.isUserFiveKeys(loggedInUser),
		};
	});

	useEffect(() => {
		if (!loggedInUser?.uid) {
			return;
		}

		const loading =
			(fleets.pending || fleet.pending) && hasTrends === undefined;

		setLoadingData(loading);

		if (!loading && fleet.value) {
			// Cache the fleet as a no trends fleet
			const trendsAvailable = StorageUtils.get(TRENDS_AVAILABLE_KEY, {})!;
			StorageUtils.set(TRENDS_AVAILABLE_KEY, {
				...trendsAvailable,
				[fleet.value.id]: hasTrends,
			});
		}
	}, [fleets, fleet, dispatch, loggedInUser, hasTrends, appType]);

	useEffect(() => {
		if (loggedInUser?.uid) {
			if (loggedInUser.portalLocale !== localeContext.code) {
				Api.postUserUpdate.submit({
					params: { userId: loggedInUser.uid },
					body: {
						portalLocale: localeContext.code,
					},
				});
				dispatch(updateUserObject({ portalLocale: localeContext.code }));
			}
		}
	}, [localeContext.code, loggedInUser, dispatch, localeContext.locale]);

	useEffect(() => {
		if (!routeAccessible) {
			NavigationUtils.go("/rewards");
		}
	}, [routeAccessible]);

	const anonymousMode = useFlag(Flags.ANONYMOUS_MODE);
	const fixedContentRef = useRef<any>();
	const fixedContentViewValue = useMemo(
		() => () => fixedContentRef.current,
		[],
	);

	const redirectToFiveKeys = useCallback(() => {
		window.location.href = `https://${EnvironmentUtils.getEnvironmentInternalName(
			false,
			"driveranalytics",
		)}.5keysengage.com`;
	}, []);

	return (
		<>
			{isFiveKeys && <FiveKeysTheme />}
			{!isFiveKeys && <BrightmileTheme />}
			<main
				className={clsx(
					"Router__content",
					headerVisible && "--has-nav",
					headerVisible && additionalFleets && "--has-multi-fleet",
					headerVisible && anonymousMode.is() && "--has-anonymous-mode",
					headerVisible && bannerNotificationVisible && "--has-notification",
				)}
			>
				<Background />
				{loadingData && (
					<div className={"Router__content-loading"}>
						<LoadingSpinner />
					</div>
				)}
				{!loadingData && React.cloneElement(routes, { key: renderId })}
				{scrollToTopVisible && <ScrollToTop />}
			</main>
			<div ref={fixedContentRef} className={"FixedContent mui-fixed"}>
				<ViewContext.Provider value={fixedContentViewValue}>
					<AboveNavContext.Provider value={true}>
						<NotificationBanner notification={portalNotification} />
						<Header key={renderId} />
						<SpinnerOverlay />
						<DebugArea />
					</AboveNavContext.Provider>
				</ViewContext.Provider>
			</div>
			<PrivacyModal />
			{isFiveKeysUser && !isFiveKeys && (
				<SplashNotification
					className={"FiveKeysEngageNotification"}
					notification={{ level: 1, message: translatedFiveKeysMessage }}
					heading={strings.FIVE_KEYS_VARIANT_POPUP_TITLE}
					cta={<Button onClick={redirectToFiveKeys}>Go to new portal</Button>}
				/>
			)}
			<SplashNotification notification={portalNotification} />
			<ToastContainer />
		</>
	);
};

const App = () => {
	const [resetKey, setResetKey] = useState(0);
	const [renderId, setRenderId] = useState(0);
	const isFiveKeys = useRef(isFiveKeysTheme());

	const dispatch = useDispatch();

	useEffect(() => {
		NavigationUtils.provideRefreshHandler((force) => {
			if (force) {
				return (window.location as any).reload(force);
			}
			setRenderId((i) => i + 1);
		});
	}, [dispatch]);

	useEffect(() => {
		const id = NavigationUtils.onHistoryChange(() => {
			setResetKey((resetKey) => resetKey + 1);
		});

		return () => NavigationUtils.clearHistoryChange(id);
	});

	return (
		<StyledEngineProvider injectFirst={true}>
			<ThemeProvider theme={theme}>
				<LocaleUtils.LocaleProvider>
					<Helmet>
						<link
							rel={"icon"}
							sizes={"32x32"}
							href={
								isFiveKeys.current
									? "/icons/favicon-5-keys.png"
									: "/icons/favicon-32x32.png"
							}
						/>

						<meta
							property={"og:title"}
							content={isFiveKeys.current ? "5Keys Engage" : "Brightmile"}
						/>
					</Helmet>
					<ErrorBoundary fatal={true} resetKey={resetKey}>
						{(topLevelError: Error | null) =>
							topLevelError ? (
								<FatalError error={topLevelError} />
							) : (
								React.createElement(routerManager.component, {
									children: (routes: any) => (
										<RouterContent renderId={renderId} routes={routes} />
									),
								} as any)
							)
						}
					</ErrorBoundary>
				</LocaleUtils.LocaleProvider>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

export default React.memo(App);
